import { useState } from 'react';
import { Button, Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import useSettingsParams from 'hooks/useSettingsParams';
import { useAuth } from 'context/AuthContext';
import { canCreateTasks } from 'utils/helpers/access/hasUserAccess';
import NewTask from '../NewTask';
import ShowTaskTable from '../ShowTaskTable';
import { TaskProvider, useTask } from '../TaskContext';
import ListToolBarActions from 'components/toolbar/ListToolBarActions';
import TaskFilter from '../shared/TaskFilter';
import SolarIcon from 'components/icons/SolarIcon';

export interface OffTasksProps {
    show: boolean;
    onHide: () => void;
    leadId?: number | null;
    objectId?: number | null;
    /** Назва заявки або об'єкта — зліва від кнопки «Нова задача» */
    contextName?: string | null;
}

const OffTasksBody = ({
    show,
    onHide,
    leadId,
    objectId,
    contextName,
}: {
    show: boolean;
    onHide: () => void;
    leadId: number | null;
    objectId: number | null;
    contextName: string | null;
}) => {
    const { t } = useTranslation(['Tasks']);
    const { auth } = useAuth();
    const { triggerListRefresh, triggerListReset } = useTask();
    const [showNewTask, setShowNewTask] = useState(false);
    const title = String(contextName || '').trim();
    const canCreate = canCreateTasks(auth?.user);

    const handleHide = () => {
        setShowNewTask(false);
        onHide();
    };

    return (
        <>
            <Offcanvas
                show={show}
                onHide={handleHide}
                placement="end"
                className="off-import off-tasks"
            >
                <Offcanvas.Header closeButton className="border-bottom">
                    <Offcanvas.Title>{t('title')}</Offcanvas.Title>
                </Offcanvas.Header>

                <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                    <div className="off-tasks-toolbar px-3 py-2 d-flex flex-column flex-sm-row justify-content-between align-items-stretch align-items-sm-center flex-shrink-0 gap-2">
                        <div className="off-tasks-context text-truncate min-w-0">
                            {title ? (
                                <>
                                    <SolarIcon name={objectId ? 'building' : 'person-lines-fill'} className="me-2 text-muted" />
                                    <span className="fw-medium">{title}</span>
                                </>
                            ) : null}
                        </div>

                        <div className="d-flex flex-wrap justify-content-end align-items-center flex-shrink-0 ms-sm-auto">
                            {canCreate && (
                            <Button
                                size="sm"
                                variant="success"
                                className="flex-shrink-0"
                                onClick={(e) => {
                                    e.currentTarget.blur();
                                    setShowNewTask(true);
                                }}
                            >
                                <SolarIcon name="plus" className="fs-14 p-0" />
                                <span className="ms-1">{t('toolNewTask')}</span>
                            </Button>
                            )}

                            <ListToolBarActions
                                onReset={triggerListReset}
                                onRefresh={triggerListRefresh}
                            />

                            <TaskFilter />
                        </div>
                    </div>

                    <ShowTaskTable leadId={leadId} objectId={objectId} />
                </Offcanvas.Body>
            </Offcanvas>

            {canCreate && (
            <NewTask
                show={showNewTask}
                onHide={() => setShowNewTask(false)}
                onCreated={() => {
                    setShowNewTask(false);
                    triggerListRefresh();
                }}
                leadId={leadId}
                objectId={objectId}
                contextName={contextName}
            />
            )}
        </>
    );
};

const OffTasks = ({
    show,
    onHide,
    leadId = null,
    objectId = null,
    contextName = null,
}: OffTasksProps) => {
    const params = useSettingsParams();
    const allowedForLead = Boolean(leadId) && params.tasksModule && params.tasksForLeads;
    const allowedForObject = Boolean(objectId) && params.tasksModule && params.tasksForObjects;
    const allowed = allowedForLead || allowedForObject;

    if (!allowed) return null;

    return (
        <TaskProvider leadId={leadId} objectId={objectId} enabled={show}>
            <OffTasksBody
                show={show}
                onHide={onHide}
                leadId={leadId}
                objectId={objectId}
                contextName={contextName}
            />
        </TaskProvider>
    );
};

export default OffTasks;
